// Libs
import React, { useContext, useEffect, useState } from 'react';
// Context
import { ModalBuilderContext } from 'core/context';
// Hooks
import usePaymentLineApi from 'core/hooks/usePaymentLineApi';
import { useToastMessage } from 'hooks';
// Components
import {
	AddIcon,
	Button,
	Divider,
	EditIcon,
	InputNumber,
	LogoIcon,
	Modal,
	Select,
	Span,
	TextField,
	Wrapper,
} from 'components';
// Utils
import { filterOption, formatNumber } from 'utils/libs';

const { Input } = TextField;
const { Option } = Select;

const OPERATIONS = {
	createLine: 'createLine',
	createItem: 'createItem',
	editItem: 'editItem',
};

const EMPTY_LINE_FORM = {
	line_item_id: undefined,
	description: '',
	amount: undefined,
	tracts: undefined,
};

const EMPTY_ITEM_FORM = {
	name: '',
	coin_id: undefined,
	amount: undefined,
	tracts: undefined,
	action: undefined,
};

// ─── Header ──────────────────────────────────────────────────────────────────

const Header = ({ operation, lineItemSelected, isSubmitting, onToggleEdit }) => {
	const title =
		operation === OPERATIONS.editItem
			? 'Editar Item'
			: operation === OPERATIONS.createItem
				? 'Nuevo Item'
				: 'Nueva línea';

	return (
		<Wrapper width='95%' height='28px' justifyContent='space-between'>
			<Span fontSize='xl'>{title}</Span>
			{!isSubmitting && lineItemSelected && operation !== OPERATIONS.createItem && (
				<EditIcon onClick={onToggleEdit} />
			)}
		</Wrapper>
	);
};

// ─── CreateLine sub-form ──────────────────────────────────────────────────────

const CreateLine = ({
	form,
	lineItems,
	selectedItem,
	needsAmount,
	needsTracts,
	onSelectLine,
	onChangeForm,
	onSubmit,
	isSubmitting,
	onCreateItem,
}) => (
	<Wrapper padding='10px 0' width='95%' flexDirection='column' alignItems='stretch'>
		<Select
			showSearch
			placeholder='Línea*'
			value={form.line_item_id}
			filterOption={filterOption}
			onChange={onSelectLine}
			style={{ marginBottom: 12 }}
			dropdownRender={menu => (
				<>
					{menu}
					<Divider style={{ margin: '4px 0' }} />
					<div
						style={{ padding: '4px 8px', cursor: 'pointer' }}
						onMouseDown={e => e.preventDefault()}
						onClick={onCreateItem}
					>
						<AddIcon />
					</div>
				</>
			)}
		>
			{lineItems.map(item => (
				<Option key={item.id} value={item.id}>
					<em>{item.name}</em>
				</Option>
			))}
		</Select>

		<Input
			id='description'
			placeholder='Descripción'
			maxLength={100}
			value={form.description}
			onChange={e => onChangeForm({ description: e.target.value })}
			style={{ marginBottom: 12 }}
		/>

		{needsAmount && (
			<InputNumber
				placeholder='Monto*'
				min={0.01}
				step={0.1}
				value={form.amount}
				formatter={value =>
					`${selectedItem.coin_symbol || ''} ${value}`.replace(
						/\B(?=(\d{3})+(?!\d))/g,
						',',
					)
				}
				parser={value => {
					let v = value.replace(selectedItem.coin_symbol || '', '');
					v = v.replace(/\$\s?|(,*)/g, '');
					v = v.replace(/ /g, '');
					return v;
				}}
				onChange={value => onChangeForm({ amount: value })}
				style={{ width: '100%', marginBottom: 12 }}
			/>
		)}

		{needsTracts && (
			<InputNumber
				placeholder='Tractos*'
				min={1}
				value={form.tracts}
				onChange={value => onChangeForm({ tracts: value })}
				style={{ width: '100%', marginBottom: 12 }}
			/>
		)}

		{selectedItem?.amount > 0 && (
			<Wrapper padding='4px 0' justifyContent='space-between' style={{ marginBottom: 6 }}>
				<Span>Monto</Span>
				<Span>{formatNumber.new(selectedItem.amount, selectedItem.coin_symbol, 2)}</Span>
			</Wrapper>
		)}
		{selectedItem?.tracts > 0 && (
			<Wrapper padding='4px 0' justifyContent='space-between' style={{ marginBottom: 6 }}>
				<Span>Tractos</Span>
				<Span>{selectedItem.tracts}</Span>
			</Wrapper>
		)}
		{selectedItem && (
			<Wrapper padding='4px 0' justifyContent='space-between' style={{ marginBottom: 12 }}>
				<Span>Acción</Span>
				<Span>{selectedItem.action_label}</Span>
			</Wrapper>
		)}

		<Wrapper justifyContent='flex-end' padding='0'>
			{isSubmitting ? (
				<LogoIcon button spin />
			) : (
				<Button mode='primary' disabled={!form.line_item_id} onClick={onSubmit}>
					Crear
				</Button>
			)}
		</Wrapper>
	</Wrapper>
);

// ─── CreateItem / EditItem sub-form ───────────────────────────────────────────

const ItemForm = ({
	operation,
	itemForm,
	coins,
	onChangeItem,
	onSubmit,
	isSubmitting,
}) => {
	const isEdit = operation === OPERATIONS.editItem;
	const selectedCoin = itemForm.coin_id
		? coins.find(c => c.id === itemForm.coin_id)
		: null;

	return (
		<Wrapper padding='10px 0' width='95%' flexDirection='column' alignItems='stretch'>
			<Input
				disabled={isEdit}
				placeholder='Item*'
				maxLength={45}
				value={itemForm.name}
				onChange={e => onChangeItem({ name: e.target.value })}
				style={{ marginBottom: 12 }}
			/>

			<Select
				disabled={isEdit}
				showSearch
				placeholder='Moneda*'
				value={itemForm.coin_id}
				filterOption={filterOption}
				onChange={value => onChangeItem({ coin_id: value, amount: undefined })}
				style={{ marginBottom: 12 }}
			>
				{coins.map(coin => (
					<Option key={coin.id} value={coin.id}>
						<em>{coin.name}</em>
					</Option>
				))}
			</Select>

			{selectedCoin && (
				<InputNumber
					placeholder='Monto*'
					min={0}
					step={0.1}
					value={itemForm.amount}
					formatter={value =>
						`${selectedCoin.symbol} ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
					}
					parser={value => {
						let v = value.replace(selectedCoin.symbol, '');
						v = v.replace(/\$\s?|(,*)/g, '');
						v = v.replace(/ /g, '');
						return v;
					}}
					onChange={value => onChangeItem({ amount: value })}
					style={{ width: '100%', marginBottom: 12 }}
				/>
			)}

			<InputNumber
				placeholder='Tractos*'
				min={1}
				value={itemForm.tracts}
				onChange={value => onChangeItem({ tracts: value })}
				style={{ width: '100%', marginBottom: 12 }}
			/>

			<Select
				disabled={isEdit}
				showSearch
				placeholder='Acción*'
				value={itemForm.action}
				filterOption={filterOption}
				onChange={value => onChangeItem({ action: value })}
				style={{ marginBottom: 12 }}
			>
				<Option value='debit'><em>Debitar</em></Option>
				<Option value='credit'><em>Acreditar</em></Option>
			</Select>

			<Wrapper justifyContent='flex-end' padding='0'>
				{isSubmitting ? (
					<LogoIcon button spin />
				) : (
					<Button mode='primary' onClick={onSubmit}>
						{isEdit ? 'Actualizar' : 'Crear'}
					</Button>
				)}
			</Wrapper>
		</Wrapper>
	);
};

// ─── Main component ───────────────────────────────────────────────────────────

const CreatePaymentLine = () => {
	const { payload: modalPayload, cleanModal } = useContext(ModalBuilderContext);
	const { paymentId, paidTo, refreshLines } = modalPayload;

	const api = usePaymentLineApi();
	const sendToast = useToastMessage();

	const [lineItems, setLineItems] = useState([]);
	const [coins, setCoins] = useState([]);
	const [isFetching, setIsFetching] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [operation, setOperation] = useState(OPERATIONS.createLine);
	const [lineForm, _setLineForm] = useState(EMPTY_LINE_FORM);
	const [itemForm, _setItemForm] = useState(EMPTY_ITEM_FORM);

	const setLineForm = values => _setLineForm(cur => ({ ...cur, ...values }));
	const setItemForm = values => _setItemForm(cur => ({ ...cur, ...values }));

	const selectedItem =
		lineForm.line_item_id &&
		lineItems.find(item => item.id === lineForm.line_item_id);

	const needsAmount = selectedItem && (!selectedItem.amount || selectedItem.amount <= 0);
	const needsTracts = selectedItem && (!selectedItem.tracts || selectedItem.tracts <= 0);

	useEffect(() => {
		Promise.all([api.getLineItems(), api.getCoins()])
			.then(([items, coinsData]) => {
				setLineItems(Array.isArray(items) ? items : []);
				setCoins(Array.isArray(coinsData) ? coinsData : []);
				setIsFetching(false);
			})
			.catch(err => {
				sendToast({ message: err.message, type: 'error' });
				setIsFetching(false);
			});
	}, []);

	const handleSelectLine = value => {
		const item = lineItems.find(i => i.id === value);
		setLineForm({
			line_item_id: value,
			amount: item?.amount > 0 ? item.amount : undefined,
			tracts: item?.tracts > 0 ? item.tracts : undefined,
		});
	};

	const handleToggleEdit = () => {
		if (operation === OPERATIONS.editItem) {
			setOperation(OPERATIONS.createLine);
			_setItemForm(EMPTY_ITEM_FORM);
		} else {
			// populate itemForm from selectedItem
			setItemForm({
				name: selectedItem?.name || '',
				coin_id: selectedItem?.coin_id,
				amount: selectedItem?.amount > 0 ? selectedItem.amount : undefined,
				tracts: selectedItem?.tracts > 0 ? selectedItem.tracts : undefined,
				action: selectedItem?.action,
			});
			setOperation(OPERATIONS.editItem);
		}
	};

	const handleGoCreateItem = () => {
		_setItemForm(EMPTY_ITEM_FORM);
		setOperation(OPERATIONS.createItem);
	};

	// Submit: create payment line
	const handleCreateLine = () => {
		if (!lineForm.line_item_id) {
			sendToast({ message: 'Selecciona una línea', type: 'warning' });
			return;
		}
		if (needsAmount && (!lineForm.amount || lineForm.amount <= 0)) {
			sendToast({ message: 'Ingresa el monto', type: 'warning' });
			return;
		}
		if (needsTracts && (!lineForm.tracts || lineForm.tracts <= 0)) {
			sendToast({ message: 'Ingresa los tractos', type: 'warning' });
			return;
		}

		setIsSubmitting(true);
		api
			.createLine({
				line_item_id: lineForm.line_item_id,
				description: lineForm.description || undefined,
				amount: selectedItem?.amount > 0 ? selectedItem.amount : lineForm.amount,
				tracts: selectedItem?.tracts > 0 ? selectedItem.tracts : lineForm.tracts,
				action: selectedItem?.action,
				apply_to: paidTo,
				payId: paymentId,
			})
			.then(() => {
				sendToast({ message: 'Línea creada', type: 'success' });
				if (refreshLines) refreshLines();
				cleanModal();
			})
			.catch(err => {
				sendToast({ message: err.message, type: 'error' });
				setIsSubmitting(false);
			});
	};

	// Submit: create new catalog item
	const handleCreateItem = () => {
		if (!itemForm.name) {
			sendToast({ message: 'Ingresa el nombre del item', type: 'warning' });
			return;
		}
		if (!itemForm.coin_id) {
			sendToast({ message: 'Selecciona la moneda', type: 'warning' });
			return;
		}
		if (!itemForm.action) {
			sendToast({ message: 'Selecciona la acción', type: 'warning' });
			return;
		}

		setIsSubmitting(true);
		api
			.createLineItem(itemForm)
			.then(() => {
				sendToast({ message: 'Item creado', type: 'success' });
				return api.getLineItems();
			})
			.then(items => {
				setLineItems(Array.isArray(items) ? items : []);
				_setItemForm(EMPTY_ITEM_FORM);
				setOperation(OPERATIONS.createLine);
				setIsSubmitting(false);
			})
			.catch(err => {
				sendToast({ message: err.message, type: 'error' });
				setIsSubmitting(false);
			});
	};

	// Submit: edit existing catalog item (amount + tracts only)
	const handleEditItem = () => {
		setIsSubmitting(true);
		api
			.updateLineItem({
				line_item_id: lineForm.line_item_id,
				amount: itemForm.amount,
				tracts: itemForm.tracts,
			})
			.then(() => {
				sendToast({ message: 'Item actualizado', type: 'success' });
				return api.getLineItems();
			})
			.then(items => {
				setLineItems(Array.isArray(items) ? items : []);
				setOperation(OPERATIONS.createLine);
				_setItemForm(EMPTY_ITEM_FORM);
				setIsSubmitting(false);
			})
			.catch(err => {
				sendToast({ message: err.message, type: 'error' });
				setIsSubmitting(false);
			});
	};

	const submitByOperation = {
		[OPERATIONS.createLine]: handleCreateLine,
		[OPERATIONS.createItem]: handleCreateItem,
		[OPERATIONS.editItem]: handleEditItem,
	};

	return (
		<Modal
			title={null}
			closable={false}
			centered={false}
			visible={true}
			onCancel={cleanModal}
			footer={null}
		>
			<Wrapper
				highBackground
				width='100%'
				padding='10px'
				margin='0'
				flexDirection='column'
				alignItems='center'
				justifyContent='flex-start'
			>
				<Header
					operation={operation}
					lineItemSelected={!!lineForm.line_item_id}
					isSubmitting={isSubmitting}
					onToggleEdit={handleToggleEdit}
				/>

				<Divider />

				{isFetching ? (
					<LogoIcon spin />
				) : operation === OPERATIONS.createLine ? (
					<CreateLine
						form={lineForm}
						lineItems={lineItems}
						selectedItem={selectedItem}
						needsAmount={needsAmount}
						needsTracts={needsTracts}
						onSelectLine={handleSelectLine}
						onChangeForm={setLineForm}
						onSubmit={handleCreateLine}
						isSubmitting={isSubmitting}
						onCreateItem={handleGoCreateItem}
					/>
				) : (
					<ItemForm
						operation={operation}
						itemForm={itemForm}
						coins={coins}
						onChangeItem={setItemForm}
						onSubmit={submitByOperation[operation]}
						isSubmitting={isSubmitting}
					/>
				)}
			</Wrapper>
		</Modal>
	);
};

export default CreatePaymentLine;
